/// Handles Olark facade -> Olark live widget, as well as repositioning things
/// to avoid collisions between Olark and our "floating component" stuff.
import { breakpoints } from "./breakpoint_events";
import { isVisible, pollUntil } from "./utils";
import { debounce } from "lodash-es";
import { trackEvent } from "./analytics.js";

const FLOATING_ID = "floating-component";
const FACADE_ID = "olark-facade";
const BOTTOM_BANNER_ID = "account-upgrade-banner";
const OLARK_WIDGET_SELECTOR = "#hbl-live-chat-wrapper, #olark-wrapper";
const OLARK_CONTAINER_SELECTOR = "#olark-container";
const OLARK_WIDGET_BUTTON_SELECTORS = [
  ".olark-launch-button-wrapper",
  ".olark-launch-button",
  ".olark-button-focus-wrapper",
];
const SMALL_MOBILE_MAX_WIDTH = 480;
const MARGIN = 8;

// "Olark widget" refers to the actual live chat widget, "facade" is our placeholder button
let olarkWidgetObserved = false;
let olarkWidgetPollCancel = null;

const getOlarkWidget = () => document.querySelector(OLARK_WIDGET_SELECTOR);

// Get the clickable button element inside the live Olark widget
const getOlarkWidgetButton = () => {
  for (const sel of OLARK_WIDGET_BUTTON_SELECTORS) {
    const el = document.querySelector(sel);
    if (el) return el;
  }
  return null;
};

const positionOlarkFacade = ({ olark, floatingButton, bottomBanner, olarkWidget }) => {
  if (!olark || olark.id !== FACADE_ID) return;

  olark.style.removeProperty("bottom");

  const floatingIsFloor = isVisible(floatingButton);
  const bannerIsFloor = isVisible(bottomBanner) && !olarkWidget;
  const floorElement = floatingIsFloor ? floatingButton : bannerIsFloor ? bottomBanner : null;

  if (!floorElement) return;

  // force reflow before measuring
  // eslint-disable-next-line no-unused-expressions
  floorElement.offsetHeight;

  const floorHeight = floorElement.offsetHeight;
  const floorBottom = parseInt(window.getComputedStyle(floorElement).bottom, 10) || 0;
  const newOlarkBottom = floorBottom + floorHeight + MARGIN;

  olark.style.setProperty("bottom", newOlarkBottom + "px", "important");
  olark.style.setProperty("position", "fixed", "important");
};

const adjustFloatingForOlarkWidget = (olark, floatingButton) => {
  if (!olark || !floatingButton) return;

  const olarkWidgetButton = getOlarkWidgetButton();
  if (!olarkWidgetButton) {
    return;
  }

  const isWideViewport = window.innerWidth > SMALL_MOBILE_MAX_WIDTH;

  if (isWideViewport) {
    // Desktop/tablet: move floating button left to avoid overlap
    const olarkWidgetButtonWidth = olarkWidgetButton.offsetWidth || 60;
    const spacing = 40;
    const rightOffset = olarkWidgetButtonWidth + spacing;

    floatingButton.style.setProperty("right", rightOffset + "px", "important");

    // Removing the bottom property lets the SCSS class's bottom property take effect again
    floatingButton.style.removeProperty("bottom");

    olark.classList.add("olark-widget-z-capped");
  } else {
    // Narrow mobile (<=480px): position floating button *above* Olark,
    // this is a compromise because if we scooted it to the left (like we do on desktop),
    // the space for the popout well/menu would be too small. Also note: I tried positioning the
    // live Olark widget above our floating component, but it took significant workarounds and
    // things kept breaking. I don't recommend trying that again! I think this compromise is OK.
    floatingButton.style.setProperty("bottom", "90px", "important");
    floatingButton.style.removeProperty("right");

    olark.classList.remove("olark-widget-z-capped");
  }
};

// We'll hide the account banner if present when Olark widget is expanded
const updateBannerVisibility = (bottomBanner, olarkWidget) => {
  if (!bottomBanner) return;
  if (olarkWidget) {
    if (!bottomBanner.style.transition) {
      bottomBanner.style.setProperty("transition", "opacity 0.3s ease-out", "important");
    }
    bottomBanner.style.setProperty("opacity", "0", "important");
    bottomBanner.style.setProperty("pointer-events", "none", "important");
  } else {
    bottomBanner.style.removeProperty("opacity");
    bottomBanner.style.removeProperty("pointer-events");
  }
};

// We move things up above the banner if the banner is visible
const positionFloatingForBanner = ({ floatingButton, bottomBanner, olarkWidget }) => {
  if (!floatingButton || !breakpoints["mobile"]) return;
  floatingButton.style.bottom = "";
  if (isVisible(bottomBanner) && !olarkWidget) {
    const bannerHeight = bottomBanner.offsetHeight;
    if (bannerHeight > 0) {
      floatingButton.style.bottom = bannerHeight + MARGIN + "px";
    }
  }
};

// Main function that calls helpers to handle repositioning. Called from various watched events.
const repositionToAvoidOverlapping = debounce(() => {
  const floatingButton = document.getElementById(FLOATING_ID);
  const bottomBanner = document.getElementById(BOTTOM_BANNER_ID);
  const olarkFacade = document.getElementById(FACADE_ID);
  const olarkWidget = getOlarkWidget();
  const olark = olarkFacade || olarkWidget;

  if (!olark && !floatingButton && !bottomBanner) return;

  updateBannerVisibility(bottomBanner, olarkWidget);
  positionFloatingForBanner({ floatingButton, bottomBanner, olarkWidget });

  if (olark) {
    if (olark.id === FACADE_ID) {
      positionOlarkFacade({ olark, floatingButton, bottomBanner, olarkWidget });
    } else {
      adjustFloatingForOlarkWidget(olark, floatingButton);
    }
  }
}, 10);

// Call repositionToAvoidOverlapping when our floating component changes (i.e. visibility change, such as in Inventory views)
const observeOurFloatingComponent = () => {
  const floating = document.getElementById(FLOATING_ID);
  if (!floating) return;
  const observer = new MutationObserver(repositionToAvoidOverlapping);
  observer.observe(floating, { attributes: true, attributeFilter: ["style", "class"] });
};

const attachOlarkWidgetObserver = (olarkWidget) => {
  if (!olarkWidget) return;
  if (olarkWidgetObserved) return;
  const observer = new MutationObserver(repositionToAvoidOverlapping);
  observer.observe(olarkWidget, {
    attributes: true,
    subtree: true,
    attributeFilter: ["class", "aria-expanded", "aria-hidden", "style"],
  });
  olarkWidgetObserved = true;
};

// loadOlark is third-party code
const loadOlark = () => {
  const olarkLoader = (o, l, a, r, k, y) => {
    if (o.olark) return;
    r = "script";
    y = l.createElement(r);
    r = l.getElementsByTagName(r)[0];
    y.async = 1;
    y.src = "//" + a;
    r.parentNode.insertBefore(y, r);
    y = o.olark = function () {
      k.s.push(arguments);
      k.t.push(+new Date());
    };
    y.extend = function (i, j) {
      y("extend", i, j);
    };
    y.identify = function (i) {
      y("identify", (k.i = i));
    };
    y.configure = function (i, j) {
      y("configure", i, j);
      k.c[i] = j;
    };
    k = y._ = { s: [], t: [+new Date()], c: {}, l: a };
  };

  olarkLoader(window, document, "static.olark.com/jsclient/loader.js");

  const olark = window.olark;
  olark.identify("1774-394-10-9547");

  olark.configure("system.hb_custom_style", {
    general: {
      fonts: ["Lato", "Helvetica", "Arial", "sans-serif"],
      corners: "",
      secondaryColor: "",
    },
  });

  if (window.currentUser && window.currentUser.email) {
    olark("api.visitor.updateEmailAddress", { emailAddress: window.currentUser.email });
  }

  olark("api.box.onExpand", () => (document.cookie = "isChatting=true; max-age=86400; path=/"));
  olark("api.box.onShrink", () => (document.cookie = "isChatting=; max-age=0; path=/"));
};

// This is also mostly third-party code, with slight modifications to integrate to repositioning
const setupOlarkFacadeBehavior = () => {
  const olarkFacade = document.getElementById(FACADE_ID);
  if (!olarkFacade && !window.skipOlarkFacade) return;

  if (!olarkFacade) {
    loadOlark();
    return;
  }

  olarkFacade.addEventListener(
    "click",
    (_ev) => {
      trackEvent("olark_click");
      loadOlark();

      const olark = window.olark;

      olark("api.box.onExpand", () => {
        olarkFacade.remove();
        setTimeout(repositionToAvoidOverlapping, 100);
      });

      olark("api.chat.onReady", () => {
        olark("api.box.expand");

        pollUntil({
          check: () => {
            const olarkWidget = getOlarkWidget();
            if (olarkWidget) {
              repositionToAvoidOverlapping();
              attachOlarkWidgetObserver(olarkWidget);
              return true;
            }
            return false;
          },
          interval: 100,
          maxAttempts: 100,
        });
      });
    },
    { once: true }
  );
};

const watchOlarkWidget = () => {
  if (olarkWidgetObserved) return;

  const tryAttach = () => {
    const olarkWidget = getOlarkWidget();
    const olarkContainer = document.querySelector(OLARK_CONTAINER_SELECTOR);

    if (olarkWidget && olarkContainer) {
      repositionToAvoidOverlapping();
      attachOlarkWidgetObserver(olarkWidget);
      return true;
    }
    return false;
  };

  if (tryAttach()) return;

  if (!olarkWidgetPollCancel) {
    olarkWidgetPollCancel = pollUntil({
      check: tryAttach,
      interval: 200,
      maxAttempts: 50,
      onGiveUp: () => {
        olarkWidgetPollCancel = null;
      },
    });
  }
};

const bindRepositioning = () => {
  document.addEventListener("partial:replace", repositionToAvoidOverlapping);
  document.addEventListener("floating-widget-visibility-changed", repositionToAvoidOverlapping);
  window.addEventListener("resize", repositionToAvoidOverlapping);

  observeOurFloatingComponent();
  watchOlarkWidget();
};

const scan = () => {
  bindRepositioning();
  setupOlarkFacadeBehavior();
  requestAnimationFrame(() => repositionToAvoidOverlapping());
};

document.addEventListener("DOMContentLoaded", scan);
